/* eslint-disable react/forbid-prop-types, react/no-unstable-nested-components */
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import Grid from '@mui/material/Grid';
import BoltIcon from '@mui/icons-material/Bolt';
import Typography from '@mui/material/Typography';
import { stringToObject } from 'relevant-shared/misc/funcJson';
import TagUtils from 'relevant-shared/misc/tagUtils';
import TextEditor from '../TagEditor/textEditor';
import OperationWrapper from '../OperationWrapper';
import ExpandSelector from '../ExpandSelector';
import JobButton from '../JobButton';
import { FormOf } from '../Wrappers';
import Disabler from '../Disabler';
import classes from '../../api/classes';
import { DebugChart, codeToPlacMap } from './utils';

const { TagData } = classes;

const isValid = (str) => {
	try {
		stringToObject(str);
	} catch (e) {
		return false;
	}
	return true;
};

class CallDebugger extends React.Component {
	constructor(props) {
		super(props);
		this.state = {};
	}

	async loadLastCall({ boostFields }, output) {
		const res = [];
		for (const { boostValues } of output) {
			for (const [scopeIdx, perPath] of _.entries(boostValues)) {
				for (const [pathIdx, [{ debugByKey, keys }, ...values]] of _.entries(perPath)) {
					const { scopeId, paths } = boostFields[scopeIdx];
					const path = paths[pathIdx];
					let keyToObj;
					if (scopeId === 'genericPlacementData') {
						keyToObj = await codeToPlacMap(keys);
					}
					res.push(...keys.map((key, idx) => ({
						key,
						value: values[idx],
						info: debugByKey[key],
						obj: keyToObj?.[key],
						scopeId,
						path,
					})));
				}
			}
		}
		return res;
	}

	renderModelInfo({ perBin, featureIdx }, model) {
		const { binLimits } = model.features[featureIdx];
		return (
			<>
				<Typography variant="h4">
					{ model.description}
				</Typography>
				<DebugChart
					yValues={perBin}
					label={({ parsed: { x, y } }) => [
						x < binLimits.length ? `< ${binLimits[x]}` : 'Remaining',
						`Bin index: ${x}`,
						`Value: ${y}`,
					]}
				/>
			</>
		);
	}

	renderLastCall({ key, value, info, obj }) {
		const { models: { BoosterModel: allModels } } = this.props;
		const name = obj?.name || obj?.domain || '[Unknown]';
		const { noOptiReason, combinedPerBin, perModel, selectedIdx } = info;
		return (
			<Disabler disabled={!!noOptiReason}>
				<ExpandSelector
					title={(
						<div style={{ fontSize: 'smaller' }}>
							<b>{name}</b> <i>({key})</i> <b>
								<span style={{ color: noOptiReason ? 'red' : 'green' }}>
									{noOptiReason || value}{noOptiReason && value !== null ? ` ${value?.toFixed?.(3) || value}` : ''}
								</span>
							</b>
						</div>
					)}
				>
					<Grid container spacing={3}>
						<Grid item xs={12}>
							<DebugChart
								yValues={(combinedPerBin || []).map((c) => c.combined)}
								color={(combinedPerBin || []).map((__, i) => (i === selectedIdx ? 'green' : 'red'))}
								label={({ parsed: { x, y } }) => [
									`Value to set: ${combinedPerBin[x]?.toSet}`,
									`Index: ${x}`,
									`Combined value: ${y}`,
								]}
							/>
						</Grid>
						<>
							{(perModel || []).map((modelInfo) => (
								<Grid item xs={12} key={modelInfo.id}>
									{this.renderModelInfo(modelInfo, _.find(allModels, { id: modelInfo.id }))}
								</Grid>
							))}
						</>
					</Grid>
				</ExpandSelector>
			</Disabler>
		);
	}

	renderInternal({ field, form, op }) {
		const { body, url, lastCall } = this.state;
		const textEditor = (
			<TextEditor
				width="100%"
				height="400px"
				{...field('body')}
				filter={(str) => { // Beautify when pasting valid JSON into empty editor
					const beautify = !body?.trim().length && isValid(str);
					return beautify ? JSON.stringify(stringToObject(str), null, 2) : str;
				}}
				satisfy={{ valid: isValid, message: 'Syntax error' }}
			/>
		);
		const jsonErr = form.hasErrors(true)?.includes('body');
		return (
			<Grid container spacing={3}>
				<Grid item xs={9}>
					{textEditor}
				</Grid>
				<Grid item xs={3} style={{ paddingLeft: jsonErr ? TextEditor.ErrorOutlinePx : 0 }}>
					<div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#f0f0f0' }}>
						<JobButton
							style={{ marginTop: 'auto', alignSelf: 'center', paddingBottom: 10 }}
							icon={<BoltIcon style={{ color: 'yellow' }} />}
							label="Make call"
							disabled={!body || jsonErr}
							fn={async () => {
								const input = { ...stringToObject(body), bstDbgMode: true };
								const response = await fetch(url, {
									method: 'POST',
									headers: { 'Content-Type': 'application/json' },
									body: JSON.stringify(input),
								});
								if (!response.ok) {
									this.setState({ lastCall: null });
									throw Error(`Server error: ${await response.text()}`);
								}
								const data = await response.json();
								op.reload(async () => this.setState({ lastCall: await this.loadLastCall(input, data) }));
							}}
						/>
					</div>
				</Grid>
				<>
					{(lastCall || []).map((obj) => (
						<Grid item xs={12} key={obj.key}>
							{this.renderLastCall(obj)}
						</Grid>
					))}
				</>
			</Grid>
		);
	}

	render() {
		return (
			<FormOf
				model={this.state}
				content={(p) => (
					<OperationWrapper
						fn={async () => {
							const tag = await TagData.getTagData({ dataId: { type: 'programmaticMasterTag' } });
							const { value: url } = tag.data.analyticsURL;
							if (!url) {
								throw Error('Analytics URL must be set in Global programmatic tag');
							}
							this.setState({ url: `${TagUtils.makeUrl(url)}/analytics/boost` });
						}}
						disableMode
						content={(op) => this.renderInternal({ ...p, op })}
					/>
				)}
			/>
		);
	}
}

CallDebugger.propTypes = {
	models: PropTypes.object.isRequired,
};

CallDebugger.defaultProps = {
};

export default CallDebugger;
